<template>
  <div class="product-management">

    <!-- 悬浮搜索和筛选面板 -->
    <div v-if="showSearchPanel" class="search-panel-overlay" @click="showSearchPanel = false">
      <div class="search-panel" @click.stop>
        <div class="search-panel-header">
          <h3>筛选条件</h3>
          <el-button size="small" text @click="showSearchPanel = false">
            <el-icon><Close /></el-icon>
          </el-button>
        </div>
        <el-divider style="margin: 10px 0;" />
        <el-form :model="searchForm" inline label-width="80px">
          <el-form-item label="产品名称">
            <el-input 
              v-model="searchForm.name" 
              placeholder="请输入产品名称" 
              clearable 
              style="width: 200px"
            ></el-input>
          </el-form-item>
          <el-form-item label="产品类型">
            <el-select 
              v-model="searchForm.category" 
              placeholder="请选择产品类型" 
              clearable 
              style="width: 150px"
            >
              <el-option label="安防设备" value="security"></el-option>
              <el-option label="环境监测" value="environment"></el-option>
              <el-option label="智能家居" value="smarthome"></el-option>
              <el-option label="健康监测" value="health"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select 
              v-model="searchForm.status" 
              placeholder="请选择状态" 
              clearable 
              style="width: 120px"
            >
              <el-option label="启用" value="active"></el-option>
              <el-option label="禁用" value="inactive"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button @click="resetSearch">
              <el-icon><Refresh /></el-icon>
              重置
            </el-button>
            <el-button type="primary" @click="searchProducts">
              <el-icon><Search /></el-icon>
              搜索
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>

    <!-- 产品列表 -->
    <el-card class="table-card">
      <template #header>
        <div class="card-header">
          <span>产品列表</span>
          <div class="table-actions">
            <el-button 
              @click="showSearchPanel = !showSearchPanel" 
              :type="showSearchPanel ? 'warning' : 'default'"
              style="margin-right: 10px;"
            >
              <el-icon><Filter /></el-icon>
              筛选
            </el-button>
            <el-button type="primary" @click="addProductVisible = true">
              <el-icon><Plus /></el-icon>
              添加产品
            </el-button>
          </div>
        </div>
      </template>

      <el-table 
        :data="filteredProducts" 
        style="width: 100%" 
        v-loading="loading"
        stripe
        border
      >
        <el-table-column label="产品图片" width="100" align="center">
          <template #default="scope">
            <el-avatar :size="60" :src="scope.row.image" shape="square" @click="showImagePreview(scope.row.image)" style="cursor: pointer;">
              <el-icon><Box /></el-icon>
            </el-avatar>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="产品名称" min-width="150"></el-table-column>
        <el-table-column prop="model" label="产品型号" min-width="120"></el-table-column>
        <el-table-column label="产品类型" width="120" align="center">
          <template #default="scope">
            <el-tag :type="getCategoryTagType(scope.row.category)" size="small">
              {{ getCategoryText(scope.row.category) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="manufacturer" label="制造商" min-width="120"></el-table-column>
        <el-table-column label="设备类型" width="120" align="center">
          <template #default="scope">
            <el-tag :type="getDeviceTypeTagType(scope.row.deviceTypeId)" size="small">
              {{ getDeviceTypeText(scope.row.deviceTypeId) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.status === 'active' ? 'success' : 'danger'" size="small">
              {{ scope.row.status === 'active' ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="设备数量" width="100" align="center">
          <template #default="scope">
            <el-link type="primary" @click="viewDevices(scope.row)">
              {{ scope.row.deviceCount }}台
            </el-link>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="160" align="center">
          <template #default="scope">
            {{ formatDateTime(scope.row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="320" align="center" fixed="right">
          <template #default="scope">
            <el-button size="small" type="primary" plain @click="$router.push(`/admin/products/${scope.row.id}`)">查看</el-button>
            <el-button size="small" type="warning" plain @click="editProduct(scope.row)">编辑</el-button>
            <el-button size="small" type="info" plain @click="editScript(scope.row)">脚本</el-button>
            <el-button 
              size="small" 
              :type="scope.row.status === 'active' ? 'danger' : 'success'" 
              plain 
              @click="toggleProductStatus(scope.row)"
            >
              {{ scope.row.status === 'active' ? '禁用' : '启用' }}
            </el-button>
            <el-button size="small" type="danger" plain @click="deleteProduct(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalProducts"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加产品对话框 -->
    <el-dialog v-model="addProductVisible" title="添加产品" width="600px" @close="resetProductForm">
      <el-form :model="productForm" :rules="productRules" ref="productFormRef" label-width="100px">
        <el-form-item label="产品名称" prop="name">
          <el-input v-model="productForm.name" placeholder="请输入产品名称" maxlength="50"></el-input>
        </el-form-item>
        <el-form-item label="产品型号" prop="model">
          <el-input v-model="productForm.model" placeholder="请输入产品型号" maxlength="30"></el-input>
        </el-form-item>
        <el-form-item label="制造商" prop="manufacturer">
          <el-input v-model="productForm.manufacturer" placeholder="请输入制造商" maxlength="50"></el-input>
        </el-form-item>
        <el-form-item label="产品类型" prop="category">
          <el-select v-model="productForm.category" placeholder="请选择产品类型" style="width: 100%">
            <el-option label="安防设备" value="security"></el-option>
            <el-option label="环境监测" value="environment"></el-option>
            <el-option label="智能家居" value="smarthome"></el-option>
            <el-option label="健康监测" value="health"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="设备类型" prop="deviceTypeId">
          <el-select v-model="productForm.deviceTypeId" placeholder="请选择设备类型" style="width: 100%">
            <el-option
              v-for="deviceType in deviceTypes"
              :key="deviceType.id"
              :label="deviceType.name"
              :value="deviceType.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="是否网关">
          <el-select v-model="productForm.isGateway" style="width: 100%">
            <el-option label="否" :value="false"></el-option>
            <el-option label="是" :value="true"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="产品描述">
          <el-input 
            v-model="productForm.description" 
            type="textarea" 
            :rows="3" 
            placeholder="请输入产品描述" 
            maxlength="200"
          ></el-input>
        </el-form-item>
        <el-form-item label="产品图片">
          <el-upload
            class="avatar-uploader"
            :show-file-list="false"
            :before-upload="beforeAddImageUpload"
            accept="image/*"
          >
            <img v-if="productForm.image" :src="productForm.image" class="avatar" />
            <el-icon v-else class="avatar-uploader-icon"><Plus /></el-icon>
          </el-upload>
          <div class="upload-tip">支持 JPG、PNG 格式，大小不超过 1MB，图片会自动压缩至600x600像素</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="addProductVisible = false">取消</el-button>
          <el-button type="primary" @click="submitProduct">确定</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 编辑产品对话框 -->
    <el-dialog v-model="editProductVisible" title="编辑产品" width="800px" @close="resetEditForm">
      <el-tabs v-model="editActiveTab">
        <!-- 基本信息标签页 -->
        <el-tab-pane label="基本信息" name="basic">
          <el-form :model="editProductForm" :rules="productRules" ref="editProductFormRef" label-width="100px">
            <el-form-item label="产品名称" prop="name">
              <el-input v-model="editProductForm.name" placeholder="请输入产品名称" maxlength="50"></el-input>
            </el-form-item>
            <el-form-item label="产品型号" prop="model">
              <el-input v-model="editProductForm.model" placeholder="请输入产品型号" maxlength="30"></el-input>
            </el-form-item>
            <el-form-item label="制造商" prop="manufacturer">
              <el-input v-model="editProductForm.manufacturer" placeholder="请输入制造商" maxlength="50"></el-input>
            </el-form-item>
            <el-form-item label="产品类型" prop="category">
              <el-select v-model="editProductForm.category" placeholder="请选择产品类型" style="width: 100%">
                <el-option label="安防设备" value="security"></el-option>
                <el-option label="环境监测" value="environment"></el-option>
                <el-option label="智能家居" value="smarthome"></el-option>
                <el-option label="健康监测" value="health"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="设备类型" prop="deviceTypeId">
              <el-select v-model="editProductForm.deviceTypeId" placeholder="请选择设备类型" style="width: 100%">
                <el-option
                  v-for="deviceType in deviceTypes"
                  :key="deviceType.id"
                  :label="deviceType.name"
                  :value="deviceType.id"
                ></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="是否网关">
              <el-select v-model="editProductForm.isGateway" style="width: 100%">
                <el-option label="否" :value="false"></el-option>
                <el-option label="是" :value="true"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="产品描述">
              <el-input
                v-model="editProductForm.description"
                type="textarea"
                :rows="3"
                placeholder="请输入产品描述"
                maxlength="200"
              ></el-input>
            </el-form-item>
            <el-form-item label="产品图片">
              <el-upload
                class="avatar-uploader"
                :show-file-list="false"
                :before-upload="beforeEditImageUpload"
                accept="image/*"
              >
                <img v-if="editProductForm.image" :src="editProductForm.image" class="avatar" />
                <el-icon v-else class="avatar-uploader-icon"><Plus /></el-icon>
              </el-upload>
              <div class="upload-tip">支持 JPG、PNG 格式，大小不超过 1MB，图片会自动压缩至600x600像素</div>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <!-- AEP配置标签页 -->
        <el-tab-pane label="设备指令配置" name="aep">
          <el-form label-width="140px">
            <el-alert
              title="AEP平台配置说明"
              type="info"
              :closable="false"
              style="margin-bottom: 20px;"
            >
              <p>配置设备在电信AEP平台的产品ID和设备控制指令参数</p>
            </el-alert>

            <el-form-item label="AEP产品ID">
              <el-input-number
                v-model="aepConfig.AEP_PRODUCT_ID"
                :min="0"
                :max="99999999"
                placeholder="请输入AEP平台的产品ID"
                style="width: 100%"
              />
              <div class="form-item-tip">该产品在电信AEP平台对应的产品ID</div>
            </el-form-item>

            <el-form-item label="Master-APIkey">
              <el-input
                v-model="aepConfig.MASTER_API_KEY"
                placeholder="请输入Master-APIkey"
                style="width: 100%"
              />
              <div class="form-item-tip">AEP平台的Master-APIkey，用于设备指令下发</div>
            </el-form-item>

            <el-divider content-position="left">消音指令配置</el-divider>
            <el-form-item label="ServiceId">
              <el-input v-model="aepConfig.command.mute.serviceId" placeholder="例如: alarmDectector" />
            </el-form-item>
            <el-form-item label="Method">
              <el-input v-model="aepConfig.command.mute.method" placeholder="例如: cmd" />
            </el-form-item>
            <el-form-item label="Data">
              <el-input v-model="aepConfig.command.mute.paras.data" placeholder="例如: 140101" />
            </el-form-item>
            <el-form-item label="DataLen">
              <el-input-number v-model="aepConfig.command.mute.paras.dataLen" :min="0" :max="100" />
            </el-form-item>

            <el-divider content-position="left">复位指令配置</el-divider>
            <el-form-item label="ServiceId">
              <el-input v-model="aepConfig.command.reset.serviceId" placeholder="例如: alarmDectector" />
            </el-form-item>
            <el-form-item label="Method">
              <el-input v-model="aepConfig.command.reset.method" placeholder="例如: cmd" />
            </el-form-item>
            <el-form-item label="Data">
              <el-input v-model="aepConfig.command.reset.paras.data" placeholder="例如: 140201" />
            </el-form-item>
            <el-form-item label="DataLen">
              <el-input-number v-model="aepConfig.command.reset.paras.dataLen" :min="0" :max="100" />
            </el-form-item>

            <el-divider content-position="left">查询指令配置</el-divider>
            <el-form-item label="ServiceId">
              <el-input v-model="aepConfig.command.query.serviceId" placeholder="例如: Command" />
            </el-form-item>
            <el-form-item label="Method">
              <el-input v-model="aepConfig.command.query.method" placeholder="例如: QueryDeviceData" />
            </el-form-item>
            <el-form-item label="参数(JSON)">
              <el-input
                v-model="aepConfig.command.query.parasJson"
                type="textarea"
                :rows="2"
                placeholder='例如: {"a": 1}'
              />
              <div class="form-item-tip">JSON格式的参数，例如: {"a": 1}</div>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="editProductVisible = false">取消</el-button>
          <el-button type="primary" @click="submitEditProduct">确定</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 图片预览对话框 -->
    <el-dialog v-model="imagePreviewVisible" title="图片预览" width="1000px" center>
      <div style="text-align: center;">
        <img :src="previewImageUrl" style="width: 60%; height: auto; min-width: 400px; border-radius: 8px;" />
      </div>
    </el-dialog>

    <!-- 产品详情对话框 -->
    <el-dialog
      v-model="productDetailVisible"
      title="产品详细信息"
      width="900px"
      @close="selectedProduct = null"
      :close-on-click-modal="false"
    >
      <div v-if="selectedProduct" class="product-detail-enhanced">
        <!-- 详细信息标签页 -->
        <el-tabs v-model="detailActiveTab" class="detail-tabs">
          <!-- 基本信息 -->
          <el-tab-pane label="基本信息" name="basic">
            <el-descriptions :column="2" border class="detail-descriptions">
              <el-descriptions-item label="产品ID" label-class-name="detail-label">
                <el-text tag="code">{{ selectedProduct.id }}</el-text>
              </el-descriptions-item>
              <el-descriptions-item label="产品型号" label-class-name="detail-label">
                {{ selectedProduct.model }}
              </el-descriptions-item>
              <el-descriptions-item label="产品名称" label-class-name="detail-label">
                {{ selectedProduct.name }}
              </el-descriptions-item>
              <el-descriptions-item label="制造商" label-class-name="detail-label">
                {{ selectedProduct.manufacturer }}
              </el-descriptions-item>
              <el-descriptions-item label="产品类型" label-class-name="detail-label">
                <el-tag :type="getCategoryTagType(selectedProduct.category)">
                  {{ getCategoryText(selectedProduct.category) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="设备类型" label-class-name="detail-label">
                <el-tag :type="getDeviceTypeTagType(selectedProduct.deviceTypeId)">
                  {{ getDeviceTypeText(selectedProduct.deviceTypeId) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="产品状态" label-class-name="detail-label">
                <el-tag :type="selectedProduct.status === 'active' ? 'success' : 'danger'">
                  {{ selectedProduct.status === 'active' ? '启用' : '禁用' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="设备数量" label-class-name="detail-label">
                <el-link type="primary" @click="viewDevices(selectedProduct)">
                  {{ selectedProduct.deviceCount }}台设备
                </el-link>
              </el-descriptions-item>
              <el-descriptions-item label="创建时间" :span="2" label-class-name="detail-label">
                <el-icon><Calendar /></el-icon>
                {{ formatDateTime(selectedProduct.createdAt) }}
              </el-descriptions-item>
              <el-descriptions-item label="更新时间" :span="2" label-class-name="detail-label">
                <el-icon><Calendar /></el-icon>
                {{ formatDateTime(selectedProduct.updatedAt) }}
              </el-descriptions-item>
              <el-descriptions-item label="产品描述" :span="2" label-class-name="detail-label">
                <div class="description-text">
                  {{ selectedProduct.description || '暂无描述' }}
                </div>
              </el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <!-- 技术规格 -->
          <el-tab-pane label="技术规格" name="specs">
            <div v-if="selectedProduct.specifications" class="specs-container">
              <el-alert
                title="AEP平台配置"
                type="info"
                :closable="false"
                show-icon
                style="margin-bottom: 20px;"
              >
                以下是该产品在电信AEP平台的技术配置信息
              </el-alert>

              <div class="specs-content">
                <h4><el-icon><Setting /></el-icon> AEP产品ID</h4>
                <div class="spec-item">
                  <span class="spec-label">产品ID：</span>
                  <el-tag type="info" effect="plain">{{ getAEPProductId() }}</el-tag>
                </div>

                <el-divider content-position="left">设备指令配置</el-divider>

                <!-- 消音指令 -->
                <div class="command-group">
                  <h5><el-icon><Mute /></el-icon> 消音指令</h5>
                  <el-descriptions :column="2" size="small" border>
                    <el-descriptions-item label="Service ID">{{ getMuteCommand()?.serviceId || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="Method">{{ getMuteCommand()?.method || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="Data">{{ getMuteCommand()?.paras?.data || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="Data Length">{{ getMuteCommand()?.paras?.dataLen || '-' }}</el-descriptions-item>
                  </el-descriptions>
                </div>

                <!-- 复位指令 -->
                <div class="command-group">
                  <h5><el-icon><Refresh /></el-icon> 复位指令</h5>
                  <el-descriptions :column="2" size="small" border>
                    <el-descriptions-item label="Service ID">{{ getResetCommand()?.serviceId || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="Method">{{ getResetCommand()?.method || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="Data">{{ getResetCommand()?.paras?.data || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="Data Length">{{ getResetCommand()?.paras?.dataLen || '-' }}</el-descriptions-item>
                  </el-descriptions>
                </div>

                <!-- 查询指令 -->
                <div class="command-group">
                  <h5><el-icon><Search /></el-icon> 查询指令</h5>
                  <el-descriptions :column="2" size="small" border>
                    <el-descriptions-item label="Service ID">{{ getQueryCommand()?.serviceId || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="Method">{{ getQueryCommand()?.method || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="Parameters" :span="2">
                      <el-text tag="code">{{ JSON.stringify(getQueryCommand()?.paras || {}) }}</el-text>
                    </el-descriptions-item>
                  </el-descriptions>
                </div>
              </div>
            </div>
            <el-empty v-else description="暂无技术规格信息" :image-size="120" />
          </el-tab-pane>

          <!-- 数据脚本 -->
          <el-tab-pane label="数据脚本" name="script">
            <div class="script-preview">
              <div class="script-header">
                <span><el-icon><Document /></el-icon> 数据转换脚本</span>
                <el-button size="small" type="primary" @click="editScript(selectedProduct)">
                  <el-icon><Edit /></el-icon> 编辑脚本
                </el-button>
              </div>
              <div v-if="selectedProduct.script" class="script-code">
                <pre><code>{{ selectedProduct.script }}</code></pre>
              </div>
              <el-empty v-else description="暂无数据转换脚本" :image-size="120">
                <el-button type="primary" @click="editScript(selectedProduct)">添加脚本</el-button>
              </el-empty>
            </div>
          </el-tab-pane>

          <!-- 统计信息 -->
          <el-tab-pane label="统计信息" name="stats">
            <div class="stats-container">
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-card shadow="hover" class="stat-card">
                    <div class="stat-item">
                      <div class="stat-icon devices">
                        <el-icon :size="32"><Monitor /></el-icon>
                      </div>
                      <div class="stat-content">
                        <div class="stat-label">设备总数</div>
                        <div class="stat-value">{{ selectedProduct.deviceCount }}</div>
                        <div class="stat-desc">台设备使用该产品</div>
                      </div>
                    </div>
                  </el-card>
                </el-col>
                <el-col :span="12">
                  <el-card shadow="hover" class="stat-card">
                    <div class="stat-item">
                      <div class="stat-icon status">
                        <el-icon :size="32"><CircleCheck /></el-icon>
                      </div>
                      <div class="stat-content">
                        <div class="stat-label">产品状态</div>
                        <div class="stat-value">{{ selectedProduct.status === 'active' ? '正常' : '禁用' }}</div>
                        <div class="stat-desc">当前运行状态</div>
                      </div>
                    </div>
                  </el-card>
                </el-col>
              </el-row>
              <el-card shadow="hover" class="timeline-card" style="margin-top: 20px;">
                <template #header>
                  <div class="card-header">
                    <span><el-icon><Clock /></el-icon> 产品时间线</span>
                  </div>
                </template>
                <el-timeline>
                  <el-timeline-item :timestamp="formatDateTime(selectedProduct.createdAt)" placement="top">
                    <el-card>
                      <h4>产品创建</h4>
                      <p>产品 "{{ selectedProduct.name }}" 已创建并加入系统</p>
                    </el-card>
                  </el-timeline-item>
                  <el-timeline-item
                    v-if="selectedProduct.updatedAt && selectedProduct.updatedAt !== selectedProduct.createdAt"
                    :timestamp="formatDateTime(selectedProduct.updatedAt)"
                    placement="top"
                  >
                    <el-card>
                      <h4>最近更新</h4>
                      <p>产品信息或配置已更新</p>
                    </el-card>
                  </el-timeline-item>
                  <el-timeline-item timestamp="当前" placement="top" type="success">
                    <el-card>
                      <h4>当前状态</h4>
                      <p>{{ selectedProduct.deviceCount }}台设备正在使用该产品型号</p>
                    </el-card>
                  </el-timeline-item>
                </el-timeline>
              </el-card>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="productDetailVisible = false">
            <el-icon><Close /></el-icon> 关闭
          </el-button>
          <el-button type="primary" @click="editProduct(selectedProduct)">
            <el-icon><Edit /></el-icon> 编辑产品
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 脚본编辑对话框 -->
    <el-dialog v-model="scriptEditVisible" title="脚本编辑" width="1200px"  center @close="resetScriptForm"   :style="{ '--el-dialog-margin-top': '10vh' }">
      <div class="script-editor-container">
        <div class="script-info">
          <h4>产品: {{ currentProduct?.name }} ({{ currentProduct?.model }})</h4>
          <p class="script-description">
            请编写JavaScript脚本，必须包含translate(input)函数用于数据转换。
            <br>该函数接收队列中的原始JSON数据，返回与平台兼容的JSON格式。
          </p>
        </div>
        
        <div class="editor-content">
          <!-- Monaco Editor 容器 -->
          <div class="editor-left">
            <div id="monaco-editor" class="monaco-editor-wrapper"></div>
          </div>
          
          <!-- 脚本测试区域 -->
          <div class="editor-right">
            <div class="script-test-panel">
              <h5>脚本测试</h5>
              <div class="test-input-output">
                <div class="test-input">
                  <label>测试输入 (JSON):</label>
                  <el-input
                    v-model="testInput"
                    type="textarea"
                    :rows="8"
                    placeholder='{"device_id": "test001", "sensor_data": {"temperature": 25.5}}'
                  ></el-input>
                </div>
                <div class="test-output">
                  <label>输出结果:</label>
                  <el-input
                    v-model="testOutput"
                    type="textarea"
                    :rows="8"
                    readonly
                    placeholder="点击测试按钮查看转换结果"
                  ></el-input>
                </div>
              </div>
              <div class="test-actions">
                <el-button @click="testScript">测试脚本</el-button>
                <el-button @click="resetTestData">重置测试数据</el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="scriptEditVisible = false">取消</el-button>
          <el-button type="primary" @click="saveScript" :loading="scriptSaving">保存脚本</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Plus, Search, Refresh, Box, Filter, Close, Calendar,
  Setting, Document, Edit, Monitor, CircleCheck, Clock, Mute
} from '@element-plus/icons-vue'
import { productApi } from '@/utils/api'
import loader from '@monaco-editor/loader'

export default {
  name: 'ProductManagement',
  components: {
    Plus, Search, Refresh, Box, Filter, Close, Calendar,
    Setting, Document, Edit, Monitor, CircleCheck, Clock, Mute
  },
  setup() {
    const loading = ref(false)
    const addProductVisible = ref(false)
    const editProductVisible = ref(false)
    const editActiveTab = ref('basic')
    const showSearchPanel = ref(false)
    const imagePreviewVisible = ref(false)
    const previewImageUrl = ref('')
    const productDetailVisible = ref(false)
    const selectedProduct = ref(null)
    const detailActiveTab = ref('basic')
    const currentPage = ref(1)
    const pageSize = ref(20)
    const totalProducts = ref(0)

    // AEP配置
    const aepConfig = ref({
      AEP_PRODUCT_ID: null,
      command: {
        mute: {
          serviceId: 'alarmDectector',
          method: 'cmd',
          paras: {
            data: '140101',
            dataLen: 3
          }
        },
        reset: {
          serviceId: 'alarmDectector',
          method: 'cmd',
          paras: {
            data: '140201',
            dataLen: 3
          }
        },
        query: {
          serviceId: 'Command',
          method: 'QueryDeviceData',
          parasJson: '{"a": 1}'
        }
      }
    })
    
    // 搜索表单
    const searchForm = ref({
      name: '',
      category: '',
      status: ''
    })
    
    // 产品表单
    const productForm = ref({
      name: '',
      model: '',
      manufacturer: '',
      category: '',
      deviceTypeId: '',
      description: '',
      image: '',
      isGateway: false
    })
    
    // 编辑产品表单
    const editProductForm = ref({
      id: null,
      name: '',
      model: '',
      manufacturer: '',
      category: '',
      deviceTypeId: '',
      description: '',
      image: '',
      isGateway: false
    })
    
    // 表单引用
    const productFormRef = ref()
    const editProductFormRef = ref()
    
    // 脚本编辑相关状态
    const scriptEditVisible = ref(false)
    const currentProduct = ref(null)
    const scriptSaving = ref(false)
    const testInput = ref('{"device_id": "test001", "sensor_data": {"temperature": 25.5}, "timestamp": "2023-12-01T10:00:00Z"}')
    const testOutput = ref('')
    let monacoEditor = null
    
    // 表单验证规则
    const productRules = ref({
      name: [
        { required: true, message: '请输入产品名称', trigger: 'blur' },
        { min: 2, max: 50, message: '产品名称长度在 2 到 50 个字符', trigger: 'blur' }
      ],
      model: [
        { required: true, message: '请输入产品型号', trigger: 'blur' },
        { min: 2, max: 30, message: '产品型号长度在 2 到 30 个字符', trigger: 'blur' }
      ],
      manufacturer: [
        { required: true, message: '请输入制造商', trigger: 'blur' }
      ],
      category: [
        { required: true, message: '请选择产品类型', trigger: 'change' }
      ],
      deviceTypeId: [
        { required: true, message: '请选择设备类型', trigger: 'change' }
      ]
    })
    
    // 产品数据
    const products = ref([])
    
    // 设备类型数据
    const deviceTypes = ref([])
    
    // 加载设备类型数据
    const loadDeviceTypes = async () => {
      try {
        const response = await productApi.getDeviceTypes()
        if (response && response.success) {
          deviceTypes.value = response.data || []
        }
      } catch (error) {
        console.error('加载设备类型失败:', error)
        deviceTypes.value = []
      }
    }
    
    // 加载产品数据
    const loadProducts = async () => {
      try {
        loading.value = true
        const params = {
          page: currentPage.value,
          size: pageSize.value
        }
        
        // 添加搜索条件
        if (searchForm.value.name) {
          params.name = searchForm.value.name
        }
        if (searchForm.value.category) {
          params.category = searchForm.value.category
        }
        if (searchForm.value.status) {
          params.status = searchForm.value.status
        }
        
        const response = await productApi.getProducts(params)
        console.log('API response:', response) // 临时调试信息
        if (response && response.success) {
          products.value = response.data || []
          totalProducts.value = response.total || 0
          console.log('Products loaded:', products.value.length, products.value) // 临时调试信息
        } else {
          console.warn('API response not successful or missing data:', response)
        }
      } catch (error) {
        console.error('加载产品数据失败:', error)
        ElMessage.error('加载产品数据失败')
        // 在错误情况下确保数组为空
        products.value = []
        totalProducts.value = 0
      } finally {
        loading.value = false
      }
    }
    
    // 计算属性 - 过滤后的产品列表（现在直接返回products，因为过滤在API层处理）
    const filteredProducts = computed(() => {
      return products.value
    })
    
    // 获取产品类型标签样式
    const getCategoryTagType = (category) => {
      const typeMap = {
        'security': 'danger',
        'environment': 'success',
        'smarthome': 'primary',
        'health': 'warning'
      }
      return typeMap[category] || 'info'
    }
    
    // 获取产品类型文本
    const getCategoryText = (category) => {
      const textMap = {
        'security': '安防设备',
        'environment': '环境监测',
        'smarthome': '智能家居',
        'health': '健康监测'
      }
      return textMap[category] || '未知'
    }
    
    // 获取设备类型标签样式
    const getDeviceTypeTagType = (deviceType) => {
      const typeMap = {
        'smoke': 'danger',
        'gas': 'warning',
        'door': 'primary',
        'pir': 'success',
        'camera': 'info',
        'temp_humidity': 'success',
        'air_quality': 'warning',
        'switch': 'primary',
        'socket': 'primary',
        'wearable': 'warning'
      }
      return typeMap[deviceType] || 'info'
    }
    
    // 获取设备类型文本
    const getDeviceTypeText = (deviceTypeId) => {
      if (!deviceTypeId) return '未知'
      const deviceTypeObj = deviceTypes.value.find(type => type.id === deviceTypeId)
      return deviceTypeObj ? deviceTypeObj.name : `未知设备类型(ID:${deviceTypeId})`
    }
    
    // 格式化日期时间
    const formatDateTime = (dateTime) => {
      if (!dateTime) return ''
      const date = new Date(dateTime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    
    // 搜索产品
    const searchProducts = async () => {
      currentPage.value = 1
      await loadProducts()
      showSearchPanel.value = false
      ElMessage.success('搜索完成')
    }
    
    // 重置搜索
    const resetSearch = async () => {
      searchForm.value = {
        name: '',
        category: '',
        status: ''
      }
      currentPage.value = 1
      await loadProducts()
      showSearchPanel.value = false
      ElMessage.success('筛选条件已重置')
    }
    
    
    // 查看产品详情
    const viewProduct = (product) => {
      selectedProduct.value = product
      productDetailVisible.value = true
    }
    
    // 编辑产品
    const editProduct = (product) => {
      editProductForm.value = { ...product }
      editActiveTab.value = 'basic'

      // 解析specifications到aepConfig
      if (product.specifications) {
        try {
          const specs = JSON.parse(product.specifications)
          if (specs.AEP_PRODUCT_ID !== undefined) {
            aepConfig.value.AEP_PRODUCT_ID = specs.AEP_PRODUCT_ID
          } else {
            aepConfig.value.AEP_PRODUCT_ID = null
          }

          if (specs.MASTER_API_KEY !== undefined) {
            aepConfig.value.MASTER_API_KEY = specs.MASTER_API_KEY
          } else {
            aepConfig.value.MASTER_API_KEY = ''
          }

          if (specs.command) {
            // 消音配置
            if (specs.command.mute) {
              aepConfig.value.command.mute.serviceId = specs.command.mute.serviceId || 'alarmDectector'
              aepConfig.value.command.mute.method = specs.command.mute.method || 'cmd'
              if (specs.command.mute.paras) {
                aepConfig.value.command.mute.paras.data = specs.command.mute.paras.data || '140101'
                aepConfig.value.command.mute.paras.dataLen = specs.command.mute.paras.dataLen || 3
              }
            }

            // 复位配置
            if (specs.command.reset) {
              aepConfig.value.command.reset.serviceId = specs.command.reset.serviceId || 'alarmDectector'
              aepConfig.value.command.reset.method = specs.command.reset.method || 'cmd'
              if (specs.command.reset.paras) {
                aepConfig.value.command.reset.paras.data = specs.command.reset.paras.data || '140201'
                aepConfig.value.command.reset.paras.dataLen = specs.command.reset.paras.dataLen || 3
              }
            }

            // 查询配置
            if (specs.command.query) {
              aepConfig.value.command.query.serviceId = specs.command.query.serviceId || 'Command'
              aepConfig.value.command.query.method = specs.command.query.method || 'QueryDeviceData'
              if (specs.command.query.paras) {
                aepConfig.value.command.query.parasJson = JSON.stringify(specs.command.query.paras)
              } else {
                aepConfig.value.command.query.parasJson = '{"a": 1}'
              }
            }
          }
        } catch (error) {
          console.error('解析specifications失败:', error)
        }
      } else {
        // 重置为默认值
        aepConfig.value.AEP_PRODUCT_ID = null
        aepConfig.value.MASTER_API_KEY = ''
        aepConfig.value.command.mute.serviceId = 'alarmDectector'
        aepConfig.value.command.mute.method = 'cmd'
        aepConfig.value.command.mute.paras.data = '140101'
        aepConfig.value.command.mute.paras.dataLen = 3
        aepConfig.value.command.reset.serviceId = 'alarmDectector'
        aepConfig.value.command.reset.method = 'cmd'
        aepConfig.value.command.reset.paras.data = '140201'
        aepConfig.value.command.reset.paras.dataLen = 3
        aepConfig.value.command.query.serviceId = 'Command'
        aepConfig.value.command.query.method = 'QueryDeviceData'
        aepConfig.value.command.query.parasJson = '{"a": 1}'
      }

      editProductVisible.value = true
    }
    
    // 切换产品状态
    const toggleProductStatus = async (product) => {
      const action = product.status === 'active' ? '禁用' : '启用'
      try {
        await ElMessageBox.confirm(
          `确定要${action}产品 "${product.name}" 吗？`,
          '确认操作',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        const newStatus = product.status === 'active' ? 'inactive' : 'active'
        await productApi.updateProductStatus(product.id, newStatus)
        product.status = newStatus
        ElMessage.success(`${action}成功`)
      } catch (error) {
        if (error !== 'cancel') {
          console.error('切换产品状态失败:', error)
          ElMessage.error('操作失败')
        } else {
          ElMessage.info('已取消操作')
        }
      }
    }
    
    // 查看设备
    const viewDevices = (product) => {
      ElMessage.info(`查看产品 "${product.name}" 的设备列表`)
    }
    
    // 删除产品
    const deleteProduct = async (product) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除产品 "${product.name}" 吗？此操作不可恢复！`,
          '确认删除',
          {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning',
            dangerouslyUseHTMLString: false
          }
        )
        
        const response = await productApi.deleteProduct(product.id)
        if (response && response.success) {
          ElMessage.success('产品删除成功')
          await loadProducts()
        } else {
          ElMessage.error(response?.message || '删除产品失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除产品失败:', error)
          ElMessage.error('删除产品失败')
        } else {
          ElMessage.info('已取消删除操作')
        }
      }
    }
    
    // 分页处理
    const handleSizeChange = async (size) => {
      pageSize.value = size
      currentPage.value = 1
      await loadProducts()
    }
    
    const handleCurrentChange = async (page) => {
      currentPage.value = page
      await loadProducts()
    }
    
    // 重置产品表单
    const resetProductForm = () => {
      productForm.value = {
        name: '',
        model: '',
        manufacturer: '',
        category: '',
        deviceTypeId: '',
        description: '',
        image: '',
        isGateway: false
      }
    }
    
    // 重置编辑表单
    const resetEditForm = () => {
      editProductForm.value = {
        id: null,
        name: '',
        model: '',
        manufacturer: '',
        category: '',
        deviceTypeId: '',
        description: '',
        image: '',
        isGateway: false
      }
    }
    
    // 提交产品
    const submitProduct = async () => {
      if (!productFormRef.value) {
        return
      }
      
      try {
        await productFormRef.value.validate()
        await productApi.createProduct(productForm.value)
        addProductVisible.value = false
        resetProductForm()
        await loadProducts()
        ElMessage.success('产品添加成功')
      } catch (error) {
        if (error !== false) { // 非表单验证错误
          console.error('添加产品失败:', error)
          ElMessage.error('添加产品失败')
        }
      }
    }
    
    // 提交编辑产品
    const submitEditProduct = async () => {
      if (!editProductFormRef.value) {
        return
      }

      try {
        await editProductFormRef.value.validate()

        // 构建specifications JSON
        const specifications = {
          AEP_PRODUCT_ID: aepConfig.value.AEP_PRODUCT_ID,
          MASTER_API_KEY: aepConfig.value.MASTER_API_KEY,
          command: {
            mute: {
              serviceId: aepConfig.value.command.mute.serviceId,
              method: aepConfig.value.command.mute.method,
              paras: {
                data: aepConfig.value.command.mute.paras.data,
                dataLen: aepConfig.value.command.mute.paras.dataLen
              }
            },
            reset: {
              serviceId: aepConfig.value.command.reset.serviceId,
              method: aepConfig.value.command.reset.method,
              paras: {
                data: aepConfig.value.command.reset.paras.data,
                dataLen: aepConfig.value.command.reset.paras.dataLen
              }
            },
            query: {
              serviceId: aepConfig.value.command.query.serviceId,
              method: aepConfig.value.command.query.method,
              paras: JSON.parse(aepConfig.value.command.query.parasJson || '{"a": 1}')
            }
          }
        }

        // 将specifications转换为JSON字符串并添加到表单数据
        const productData = {
          ...editProductForm.value,
          specifications: JSON.stringify(specifications)
        }

        await productApi.updateProduct(productData.id, productData)
        editProductVisible.value = false
        resetEditForm()
        await loadProducts()
        ElMessage.success('产品更新成功')
      } catch (error) {
        if (error !== false) { // 非表单验证错误
          console.error('更新产品失败:', error)
          ElMessage.error('更新产品失败')
        }
      }
    }
    
    // 添加产品时的图片上传处理
    const beforeAddImageUpload = (file) => {
      const isJPGOrPNG = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg'
      const isLt1M = file.size / 1024 / 1024 < 1 // 限制为1MB
      
      if (!isJPGOrPNG) {
        ElMessage.error('上传图片只能是 JPG 或 PNG 格式!')
        return false
      }
      if (!isLt1M) {
        ElMessage.error('上传图片大小不能超过 1MB!')
        return false
      }
      
      // 创建预览URL并压缩图片
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      img.onload = () => {
        // 设置最大尺寸
        const maxWidth = 600
        const maxHeight = 600
        let { width, height } = img
        
        // 计算缩放比例
        if (width > height) {
          if (width > maxWidth) {
            height = height * (maxWidth / width)
            width = maxWidth
          }
        } else {
          if (height > maxHeight) {
            width = width * (maxHeight / height)
            height = maxHeight
          }
        }
        
        canvas.width = width
        canvas.height = height
        
        // 绘制压缩后的图片
        ctx.drawImage(img, 0, 0, width, height)
        
        // 转换为base64，质量设为0.9
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.9)
        productForm.value.image = compressedDataUrl
        
        ElMessage.success('图片上传成功')
      }
      
      img.src = URL.createObjectURL(file)
      return false // 阻止自动上传
    }
    
    // 编辑产品时的图片上传处理
    const beforeEditImageUpload = (file) => {
      const isJPGOrPNG = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg'
      const isLt1M = file.size / 1024 / 1024 < 1 // 限制为1MB
      
      if (!isJPGOrPNG) {
        ElMessage.error('上传图片只能是 JPG 或 PNG 格式!')
        return false
      }
      if (!isLt1M) {
        ElMessage.error('上传图片大小不能超过 1MB!')
        return false
      }
      
      // 创建预览URL并压缩图片
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      img.onload = () => {
        // 设置最大尺寸
        const maxWidth = 600
        const maxHeight = 600
        let { width, height } = img
        
        // 计算缩放比例
        if (width > height) {
          if (width > maxWidth) {
            height = height * (maxWidth / width)
            width = maxWidth
          }
        } else {
          if (height > maxHeight) {
            width = width * (maxHeight / height)
            height = maxHeight
          }
        }
        
        canvas.width = width
        canvas.height = height
        
        // 绘制压缩后的图片
        ctx.drawImage(img, 0, 0, width, height)
        
        // 转换为base64，质量设为0.9
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.9)
        editProductForm.value.image = compressedDataUrl
        
        ElMessage.success('图片上传成功')
      }
      
      img.src = URL.createObjectURL(file)
      return false // 阻止自动上传
    }
    
    // 显示图片预览
    const showImagePreview = (imageUrl) => {
      if (imageUrl) {
        previewImageUrl.value = imageUrl
        imagePreviewVisible.value = true
      }
    }
    
    // 脚本编辑相关方法
    const editScript = async (product) => {
      currentProduct.value = product
      scriptEditVisible.value = true
      
      await nextTick()
      initMonacoEditor(product.script || getDefaultScript())
    }
    
    const initMonacoEditor = async (content) => {
      if (monacoEditor) {
        monacoEditor.dispose()
      }
      
      try {
        const monaco = await loader.init()
        
        monacoEditor = monaco.editor.create(document.getElementById('monaco-editor'), {
          value: content,
          language: 'javascript',
          theme: 'vs-dark',
          automaticLayout: true,
          fontSize: 14,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          lineNumbers: 'on',
          folding: true,
          formatOnPaste: true,
          formatOnType: true
        })
      } catch (error) {
        console.error('初始化Monaco Editor失败:', error)
        ElMessage.error('代码编辑器初始化失败')
      }
    }
    
    const getDefaultScript = () => {
      return `// 数据转换脚本
function translate(input) {
    // input 是从队列接收到的原始JSON数据
    // 返回与平台兼容的JSON格式
    try {
        // 基础数据转换逻辑
        const result = {
            deviceId: input.device_id || input.deviceId,
            deviceType: input.device_type || input.deviceType,
            timestamp: input.timestamp || new Date().toISOString(),
            data: input.data || input
        };
        
        // 根据设备类型进行特殊处理
        if (input.sensor_data) {
            result.sensorData = input.sensor_data;
        }
        
        if (input.alarm_type) {
            result.alarmType = input.alarm_type;
            result.alarmLevel = input.alarm_level || 2;
        }
        
        return result;
    } catch (error) {
        console.error("数据转换错误:", error);
        return input; // 转换失败时返回原始数据
    }
}`
    }
    
    const testScript = () => {
      try {
        const scriptContent = monacoEditor.getValue()
        const jsonString = testInput.value
        
        // 验证输入是有效的JSON
        JSON.parse(jsonString) // 验证但不使用解析结果
        
        // 创建一个与后端一致的执行环境，传递JSON字符串
        const scriptFunction = new Function('jsonString', `
          ${scriptContent}
          
          // 模拟后端的safeTranslate包装器
          function safeTranslate(jsonString) {
            var input = JSON.parse(jsonString);
            var result = translate(input);
            return typeof result === 'string' ? result : JSON.stringify(result);
          }
          
          return safeTranslate(jsonString);
        `)
        
        const result = scriptFunction(jsonString)
        
        // 如果结果是字符串格式的JSON，则格式化显示
        try {
          const parsedResult = JSON.parse(result)
          testOutput.value = JSON.stringify(parsedResult, null, 2)
        } catch (e) {
          testOutput.value = result
        }
        
        ElMessage.success('脚本测试成功')
      } catch (error) {
        testOutput.value = `错误: ${error.message}`
        ElMessage.error('脚本测试失败: ' + error.message)
      }
    }
    
    const resetTestData = () => {
      testInput.value = `{
  "messageId": 17,
  "signalPower": 5503.6,
  "rsrq": 5811.1,
  "SNR": 870.5,
  "PCI": 15105,
  "CSQ": 27,
  "ECL": 255,
  "CellID": 39660033,
  "IMEI": "863270071710466",
  "IMSI": "460240261381192",
  "ICCID": "89860846162480051192",
  "heartTime": 230,
  "pkgSn": 29,
  "devType": 20,
  "dataLen1": 2048,
  "identificationCode": "863270071710466",
  "dataVal": "00012A001C3C1656",
  "status": {
    "smokeAlarm": true,
    "alarmSilenced": false,
    "tamperState": false,
    "batteryLow": false,
    "smokeSensorFault": false,
    "temperatureAlarm": false,
    "tempSensorFault": false,
    "humanSensorFault": false,
    "humanSensingActive": false,
    "humanSensingAlarm": false
  },
  "evt": 42,
  "evtDescription": "远程消音请求",
  "NB_errNum": 0,
  "NB_errDescription": "无故障",
  "batVol": 2.8,
  "batValue": 60,
  "temperature": 22,
  "smokeValue": 0.86
}`
      testOutput.value = ''
    }
    
    const saveScript = async () => {
      try {
        scriptSaving.value = true
        const scriptContent = monacoEditor.getValue()
        
        const response = await productApi.updateProductScript(currentProduct.value.id, scriptContent)
        
        if (response.success) {
          ElMessage.success('脚本保存成功')
          scriptEditVisible.value = false
          await loadProducts() // 重新加载产品列表
        } else {
          ElMessage.error(response.message || '脚本保存失败')
        }
      } catch (error) {
        console.error('保存脚本失败:', error)
        ElMessage.error('脚本保存失败: ' + error.message)
      } finally {
        scriptSaving.value = false
      }
    }
    
    const resetScriptForm = () => {
      if (monacoEditor) {
        monacoEditor.dispose()
        monacoEditor = null
      }
      currentProduct.value = null
      testInput.value = '{"device_id": "test001", "sensor_data": {"temperature": 25.5}, "timestamp": "2023-12-01T10:00:00Z"}'
      testOutput.value = ''
    }
    
    // 解析产品规格中的AEP产品ID
    const getAEPProductId = () => {
      if (!selectedProduct.value?.specifications) return '未配置'
      try {
        const specs = JSON.parse(selectedProduct.value.specifications)
        return specs.AEP_PRODUCT_ID || '未配置'
      } catch (e) {
        return '解析失败'
      }
    }

    // 获取消音指令配置
    const getMuteCommand = () => {
      if (!selectedProduct.value?.specifications) return null
      try {
        const specs = JSON.parse(selectedProduct.value.specifications)
        return specs.command?.mute || null
      } catch (e) {
        return null
      }
    }

    // 获取复位指令配置
    const getResetCommand = () => {
      if (!selectedProduct.value?.specifications) return null
      try {
        const specs = JSON.parse(selectedProduct.value.specifications)
        return specs.command?.reset || null
      } catch (e) {
        return null
      }
    }

    // 获取查询指令配置
    const getQueryCommand = () => {
      if (!selectedProduct.value?.specifications) return null
      try {
        const specs = JSON.parse(selectedProduct.value.specifications)
        return specs.command?.query || null
      } catch (e) {
        return null
      }
    }

    // 组件挂载时初始化数据
    onMounted(() => {
      loadProducts()
      loadDeviceTypes()
    })

    return {
      loading,
      addProductVisible,
      editProductVisible,
      editActiveTab,
      aepConfig,
      showSearchPanel,
      currentPage,
      pageSize,
      totalProducts,
      searchForm,
      productForm,
      editProductForm,
      productRules,
      products,
      filteredProducts,
      deviceTypes,
      loadDeviceTypes,
      getCategoryTagType,
      getCategoryText,
      getDeviceTypeTagType,
      getDeviceTypeText,
      formatDateTime,
      searchProducts,
      resetSearch,
      viewProduct,
      editProduct,
      toggleProductStatus,
      deleteProduct,
      viewDevices,
      handleSizeChange,
      handleCurrentChange,
      resetProductForm,
      resetEditForm,
      submitProduct,
      submitEditProduct,
      beforeAddImageUpload,
      beforeEditImageUpload,
      loadProducts,
      productFormRef,
      editProductFormRef,
      imagePreviewVisible,
      previewImageUrl,
      productDetailVisible,
      selectedProduct,
      detailActiveTab,
      showImagePreview,
      getAEPProductId,
      getMuteCommand,
      getResetCommand,
      getQueryCommand,
      // 脚本编辑相关
      scriptEditVisible,
      currentProduct,
      scriptSaving,
      testInput,
      testOutput,
      editScript,
      testScript,
      resetTestData,
      saveScript,
      resetScriptForm
    }
  }
}
</script>

<style scoped>
.product-management {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}


/* 搜索面板样式 */
.search-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 100px;
}

.search-panel {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 1200px;
  min-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 0;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0 20px;
}

.search-panel-header h3 {
  margin: 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.search-panel :deep(.el-form) {
  padding: 0 20px 20px 20px;
}

.search-panel :deep(.el-form-item) {
  margin-right: 20px;
  margin-bottom: 0;
}

.search-panel :deep(.el-form-item:last-child) {
  margin-right: 0;
}

.search-panel :deep(.el-form-item__content) {
  flex: none;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .search-panel {
    width: 95%;
    min-width: 600px;
  }
  
  .search-panel :deep(.el-form-item) {
    margin-right: 15px;
  }
}

@media (max-width: 768px) {
  .search-panel {
    width: 98%;
    min-width: 0;
  }
  
  .search-panel :deep(.el-form) {
    padding: 0 15px 15px 15px;
  }
  
  .search-panel :deep(.el-form-item) {
    margin-right: 10px;
    margin-bottom: 10px;
  }
  
  .search-panel :deep(.el-form-item:last-child) {
    margin-bottom: 0;
  }
}

.table-card {
  margin-bottom: 20px;
}

/* 头部操作按钮样式优化 */
.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-actions {
  display: flex;
  gap: 10px;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.dialog-footer {
  text-align: right;
}

.avatar-uploader .avatar {
  width: 80px;
  height: 80px;
  display: block;
  border-radius: 6px;
}

.avatar-uploader .el-upload {
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--el-transition-duration-fast);
}

.avatar-uploader .el-upload:hover {
  border-color: var(--el-color-primary);
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 80px;
  height: 80px;
  text-align: center;
  line-height: 80px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.4;
}

.form-item-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.5;
}

/* 产品详情增强样式 */
.product-detail-enhanced {
  padding: 0;
}

.detail-tabs {
  margin-top: 0;
}

.detail-descriptions {
  margin-top: 10px;
}

:deep(.detail-label) {
  font-weight: 600;
  color: #606266;
  background-color: #fafafa !important;
}

.description-text {
  line-height: 1.8;
  color: #606266;
}

/* 技术规格样式 */
.specs-container {
  padding: 10px;
}

.specs-content h4 {
  margin: 20px 0 15px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.spec-item {
  margin-bottom: 15px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.spec-label {
  font-weight: 600;
  color: #606266;
  margin-right: 10px;
}

.command-group {
  margin-bottom: 25px;
  padding: 15px;
  background-color: #fafbfc;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.command-group h5 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 脚本预览样式 */
.script-preview {
  padding: 10px;
}

.script-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e4e7ed;
}

.script-header span {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.script-code {
  background-color: #1e1e1e;
  border-radius: 8px;
  padding: 20px;
  overflow-x: auto;
}

.script-code pre {
  margin: 0;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}

.script-code code {
  color: #d4d4d4;
  font-size: 13px;
  line-height: 1.6;
}

/* 统计信息样式 */
.stats-container {
  padding: 10px;
}

.stat-card {
  border-radius: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 10px;
}

.stat-icon {
  width: 70px;
  height: 70px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon.devices {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.stat-icon.status {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 5px;
}

.stat-desc {
  font-size: 12px;
  color: #909399;
}

.timeline-card .card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.timeline-card :deep(.el-timeline-item__content) h4 {
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.timeline-card :deep(.el-timeline-item__content) p {
  margin: 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}

/* 脚本编辑对话框样式 */
.el-dialog {
  --el-dialog-margin-top: 10vh !important;
}


.script-editor-dialog {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  margin: 0 !important;
  overflow: hidden !important;
  z-index: 2000 !important;
}

.script-editor-dialog .el-dialog.el-dialog--center {
  margin: 0 !important;
  position: relative !important;
  top: auto !important;
  left: auto !important;
  transform: none !important;
  width: 1200px !important;
  height: calc(100vh - 10px) !important;
  max-height: calc(100vh - 10px) !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

.script-editor-dialog .el-dialog {
  margin: 0 !important;
  height: 100% !important;
  max-height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

.script-editor-dialog .el-dialog__header {
  flex-shrink: 0;
  padding: 15px 20px 10px;
}

.script-editor-dialog .el-dialog__body {
  flex: 1;
  padding: 0 20px;
  overflow: hidden;
}

.script-editor-dialog .el-dialog__footer {
  flex-shrink: 0;
  padding: 10px 20px 15px;
}

/* 脚本编辑器样式 */
.script-editor-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.script-info {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f0f9ff;
  border-radius: 6px;
  border-left: 4px solid #409eff;
}

.script-info h4 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 16px;
}

.script-description {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.editor-content {
  display: flex;
  flex: 1;
  gap: 20px;
  height: 100%;
}

.editor-left {
  flex: 2;
  display: flex;
  flex-direction: column;
}

.editor-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 350px;
}

.monaco-editor-wrapper {
  height: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  overflow: hidden;
}

.script-test-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  padding: 15px;
  background-color: #fafafa;
}

.script-test-panel h5 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.test-input-output {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 15px;
}

.test-input,
.test-output {
  flex: 1;
}

.test-input label,
.test-output label {
  display: block;
  margin-bottom: 8px;
  color: #606266;
  font-weight: 500;
  font-size: 14px;
}

.test-actions {
  margin-top: auto;
  text-align: center;
}

.test-actions .el-button {
  margin: 0 8px;
}

/* 响应式调整 */
@media (max-width: 1024px) {
  .script-editor-dialog .el-dialog.el-dialog--center {
    width: 95% !important;
    height: calc(100vh - 5px) !important;
    max-height: calc(100vh - 5px) !important;
  }
  
  .editor-content {
    flex-direction: column;
    gap: 15px;
  }
  
  .editor-left {
    flex: none;
    height: 45%;
  }
  
  .editor-right {
    flex: none;
    height: 45%;
    min-width: auto;
  }
}

@media (max-width: 768px) {
  .script-editor-dialog .el-dialog.el-dialog--center {
    width: 98% !important;
    height: calc(100vh - 2px) !important;
    max-height: calc(100vh - 2px) !important;
  }
  
  .script-editor-dialog .el-dialog__header {
    padding: 10px 15px 5px;
  }
  
  .script-editor-dialog .el-dialog__body {
    padding: 0 15px;
  }
  
  .script-editor-dialog .el-dialog__footer {
    padding: 5px 15px 10px;
  }
  
  .editor-left {
    height: 50%;
  }
  
  .editor-right {
    height: 40%;
  }
}
</style>